/**
 * AtlasConceptFinder — the compact typeahead search field on the Atlas
 * ("Neural Net") page. Solves the "can't scroll through 264 orbs"
 * problem — the operator types a Topic/Sub-Topic/Concept name and the
 * dropdown lets them jump straight to it.
 *
 * Contract: GET /api/atlas/search?topic=<partition>&q=<query>&limit=<n>
 *   → { results: [{ kind, id, label, tag, score, meta }, ...] }
 *
 * Behaviour:
 *   • 250 ms debounce on keystrokes to keep the backend chatter light.
 *   • Keyboard nav: ↑ / ↓ moves through the list, Enter selects, Esc
 *     clears + closes.
 *   • On selection:
 *       kind=concept   → parent's onSelectConcept(id) + view switch
 *       kind=sub_topic → parent's onSelectSubTopic(entity_type)
 *       kind=topic     → parent's onSelectTopic(partition)
 *   • Empty / no-match state is quiet; failure is a small rose tag.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import authedAxios from '../../lib/authedAxios';
import { Search, Loader2, X, ChevronRight } from 'lucide-react';

const API_ROOT = (
  process.env.REACT_APP_BACKEND_URL || ''
).replace(/\/$/, '') + '/api';

const KIND_META = {
  concept:   { label: 'Concept',    dot: 'bg-cyan-300',    ring: 'border-cyan-400/50' },
  sub_topic: { label: 'Sub-Topic',  dot: 'bg-amber-300',   ring: 'border-amber-400/50' },
  topic:     { label: 'Topic',      dot: 'bg-emerald-300', ring: 'border-emerald-400/50' },
};

export const AtlasConceptFinder = ({
  topic,
  onSelectConcept,
  onSelectSubTopic,
  onSelectTopic,
  onSearchStart,
}) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);

  // Debounced backend call. Race-safe via a monotonically-increasing
  // sequence number — late responses to older keystrokes are dropped.
  const fetchResults = useCallback((needle) => {
    if (!needle || !needle.trim()) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }
    if (!topic) {
      setResults([]);
      setError('Pick a Topic first');
      setLoading(false);
      return;
    }
    const mySeq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    authedAxios.get(`${API_ROOT}/atlas/search`, {
      params: { topic, q: needle, limit: 20 },
      timeout: 8000,
    })
      .then((res) => {
        if (mySeq !== requestSeqRef.current) return;
        setResults(res?.data?.results || []);
        setActiveIdx(0);
      })
      .catch((e) => {
        if (mySeq !== requestSeqRef.current) return;
        setError(e?.response?.data?.detail || e.message || 'Search failed');
        setResults([]);
      })
      .finally(() => {
        if (mySeq === requestSeqRef.current) setLoading(false);
      });
  }, [topic]);

  // Debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(q), 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, fetchResults]);

  // Click-outside → close dropdown but keep the query in the box.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const select = (r) => {
    if (!r) return;
    if (r.kind === 'concept'   && onSelectConcept)   onSelectConcept(r.id);
    if (r.kind === 'sub_topic' && onSelectSubTopic)  onSelectSubTopic(r.id, r);
    if (r.kind === 'topic'     && onSelectTopic)     onSelectTopic(r.id);
    setOpen(false);
    setQ('');
    setResults([]);
    if (inputRef.current) inputRef.current.blur();
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[activeIdx]) select(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setQ('');
      setResults([]);
      setOpen(false);
      if (inputRef.current) inputRef.current.blur();
    }
  };

  const showDropdown = open && (loading || error || results.length > 0 || q.trim().length > 0);

  return (
    <div
      ref={boxRef}
      data-testid="atlas-concept-finder"
      className="relative min-w-[280px] flex-1"
    >
      <div
        className={
          'flex items-center gap-2 rounded border bg-slate-900/60 px-3 py-1 ' +
          'transition-colors focus-within:border-cyan-400 ' +
          (error ? 'border-rose-400/40' : 'border-cyan-400/40')
        }
      >
        <Search className="h-3.5 w-3.5 text-cyan-400/70" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          data-testid="atlas-concept-finder-input"
          value={q}
          onChange={(e) => {
            const v = e.target.value;
            // Operator direction: starting a new search clears the
            // "New since refresh" amber highlights so the map stops
            // shouting at the operator.
            if (v && !q && typeof onSearchStart === 'function') {
              onSearchStart();
            }
            setQ(v);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Find a Concept, Sub-Topic or Topic…"
          className="w-full min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-cyan-100 outline-none placeholder-cyan-400/40"
          aria-label="Find a Concept, Sub-Topic or Topic"
          aria-controls="atlas-concept-finder-listbox"
          aria-expanded={showDropdown}
        />
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-300" aria-hidden="true" />
        ) : q ? (
          <button
            type="button"
            data-testid="atlas-concept-finder-clear"
            onClick={() => { setQ(''); setResults([]); setError(null); inputRef.current?.focus(); }}
            aria-label="Clear search"
            className="text-cyan-400/50 hover:text-cyan-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {showDropdown && (
        <div
          id="atlas-concept-finder-listbox"
          data-testid="atlas-concept-finder-listbox"
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[440px] overflow-y-auto rounded-lg border border-cyan-400/40 bg-slate-950/95 py-1 shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur"
        >
          {error && (
            <div data-testid="atlas-concept-finder-error" className="px-3 py-2 text-[12px] text-rose-200">
              {error}
            </div>
          )}
          {!error && loading && (
            <div className="px-3 py-2 text-[12px] italic text-cyan-300/70">
              Searching…
            </div>
          )}
          {!error && !loading && q.trim() && results.length === 0 && (
            <div
              data-testid="atlas-concept-finder-no-results"
              className="px-3 py-2 text-[12px] italic text-cyan-300/50"
            >
              No matches for &ldquo;{q}&rdquo;
            </div>
          )}
          {!error && results.map((r, idx) => {
            const meta = KIND_META[r.kind] || KIND_META.concept;
            const active = idx === activeIdx;
            return (
              <button
                type="button"
                key={`${r.kind}-${r.id}-${idx}`}
                data-testid={`atlas-concept-finder-result-${idx}`}
                role="option"
                aria-selected={active}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => select(r)}
                className={
                  'group flex w-full items-center gap-3 border-l-2 px-3 py-2 text-left transition-colors ' +
                  (active
                    ? 'border-cyan-400 bg-cyan-500/10'
                    : 'border-transparent hover:bg-cyan-500/5')
                }
              >
                <span
                  className={'h-2 w-2 flex-shrink-0 rounded-full ' + meta.dot}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-[13px] text-cyan-100" title={r.label}>
                      {r.label}
                    </span>
                    <span
                      className={
                        'flex-shrink-0 rounded border px-1 py-[1px] font-mono text-[9px] uppercase tracking-widest text-cyan-300/70 ' +
                        meta.ring
                      }
                    >
                      {meta.label}
                    </span>
                  </div>
                  {r.tag && (
                    <div className="mt-0.5 truncate text-[10px] italic text-cyan-400/60">
                      {r.tag}
                    </div>
                  )}
                </div>
                <ChevronRight
                  className={
                    'h-3.5 w-3.5 flex-shrink-0 transition-opacity ' +
                    (active ? 'opacity-100 text-cyan-200' : 'opacity-0 group-hover:opacity-80 text-cyan-300/60')
                  }
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AtlasConceptFinder;
